/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { footerStaticItems } from '../components/footer/helper'
import { MenuItem } from '../components/menu/navbar/navBarItems'
import { IDH, topicIdMap } from '../constants'
import { useAppSelector } from '../store'
import { FooterPayload, StaticNavItems } from '../types'

type TUseGetMenuItems = (opt: {
  footer?: boolean
  staticNavItems?: boolean
  pageNotFoundItems?: boolean
  navTitles?: boolean
}) => FooterPayload | StaticNavItems | StaticNavItems[] | [MenuItem[], MenuItem]

export const useGetMenuItems: TUseGetMenuItems = ({ footer, staticNavItems, pageNotFoundItems, navTitles }) => {
  const { categories } = useAppSelector((state) => state.categoryReducer)
  const { settings } = useAppSelector((state) => state.appConfigReducer)
  const { locale } = settings
  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (!locale) return
    i18n.changeLanguage(locale.language)
  }, [i18n, locale])

  const constructCategories = useCallback(
    () =>
      categories.map((cat, i) => ({
        isTitle: false,
        title: cat.name,
        url: `/shop?categoryId=${cat.siteCategoryId}`,
      })),
    [categories]
  )
  const constructFooterPayload = useCallback(
    (): FooterPayload => ({
      ...footerStaticItems,
      items: [
        {
          isTitle: true,
          title: t('Common_shop'),
          subTitle: [
            {
              isTitle: false,
              title: t('Categories_trending'),
              url: '/shop?category=trending',
            },
            {
              isTitle: false,
              title: t('Categories_topDeals'),
              url: '/shop?category=top deals',
            },
            ...constructCategories(),
          ],
          url: '/shop',
        },
        {
          isTitle: true,
          title: t('Common_help'),
          role: 'help',
          subTitle: [
            {
              isTitle: false,
              title: 'FAQ',
              url: '/help#faq',
            },
            {
              isTitle: false,
              title: t('Common_yourAccount'),
              url: `/help?helpCategoryId=${topicIdMap.yourAccount}`,
            },
            {
              isTitle: false,
              title: t('Common_yourOrders'),
              url: `/help?helpCategoryId=${topicIdMap.yourOrders}`,
            },
            {
              isTitle: false,
              title: t('Common_accessGuarantee'),
              url: `/help?helpCategoryId=${topicIdMap.accessGuarantee}`,
            },
            {
              isTitle: false,
              title: t('Common_downloadInstallation'),
              url: `/help?helpCategoryId=${topicIdMap.download}`,
            },
            {
              isTitle: false,
              title: t('Common_pricingBilling'),
              url: `/help?helpCategoryId=${topicIdMap.pricing}`,
            },
            {
              isTitle: false,
              title: t('Common_policiesSecurity'),
              url: `/help?helpCategoryId=${topicIdMap.policies}`,
            },
          ],
          url: '/help',
        },
        {
          isTitle: true,
          title: t('Common_contactUs'),
          url: '/help#support',
          role: 'help',
        },
        {
          isTitle: true,
          title: t('Common_sellWithUs'),
          url: '/sell-with-us',
        },
        {
          isTitle: true,
          title: t('Common_blog'),
          url: '/blogs',
        },
        {
          isTitle: true,
          title: t('Common_about'),
          url: '/about',
        },
      ],
    }),
    [locale, categories, t]
  )

  const constructSpecialCategories = useCallback(
    () => ({
      title: t('Categories_featured'),
      url: '/shop?category=featured',
      items: [
        {
          title: t('Categories_trending'),
          url: '/shop?category=trending',
          id: 1,
        },
        {
          title: t('Categories_topDeals'),
          url: '/shop?category=top deals',
          id: 2,
        },
        {
          title: t('Categories_trendingProductsForFaculty'),
          url: '/shop?category=faculty trending',
          id: 3,
        },
      ],
    }),
    [locale, t]
  )

  const constructPageNotFoundQuickLinkItems = useCallback(
    () => [
      {
        title: t('Common_home'),
        url: '/',
      },
      {
        title: t('Common_shop'),
        url: '/shop',
      },
      {
        title: t('Common_schools'),
        url: '/school',
      },
      {
        title: t('Common_help'),
        url: '/help',
      },
    ],
    [locale, t]
  )

  const constructNavHeaderItems = useCallback(() => {
    const rightMenuItems = [
      { title: t('Common_shop'), role: 'Shop', href: '/shop', shopDropdown: true },
      {
        title: t('Common_students'),
        role: 'Students',
        href: '/shop?userGroup=students',
        dropdownMenu: [{ title: t('Common_schools'), url: '/school' }],
      },
      {
        title: t('Common_faculty'),
        href: '/faculty',
        role: 'Faculty',
        dropdownMenu: [
          {
            title: t('Common_facultyProducts'),
            url: '/shop?userGroup=faculty',
          },
          {
            title: t('Common_bulkOrder'),
            url: '/bulk-orders',
          },
        ],
      },
      { title: t('Common_vendors'), href: '/sell-with-us' },
      { title: t('Common_help'), href: '/help' },
    ]
    const leftMenuItems = {
      title: t('Common_SignIn'),
      href: '/account',
      role: 'Sign-In',
      dropdownMenu: [
        {
          title: t('Common_accountInformation'),
          url: '/account',
          hiddenWhenUserIsGuest: true,
        },
        { title: t('Common_orderHistory'), url: '/account/order-history' },
        {
          title: t('Common_changePassword'),
          url: `${IDH.ENDPOINTS.FORGOTPASSWORD}?returnUrl=${window.location.href}`,
          external: true,
          hiddenWhenUserIsGuest: true,
        },
        { title: t('Common_logout'), url: '/logout' },
      ],
    }

    return [rightMenuItems, leftMenuItems]
  }, [locale, t])

  return footer
    ? (constructFooterPayload() as FooterPayload)
    : staticNavItems
    ? (constructSpecialCategories() as StaticNavItems)
    : pageNotFoundItems
    ? (constructPageNotFoundQuickLinkItems() as StaticNavItems[])
    : (constructNavHeaderItems() as [MenuItem[], MenuItem])
}
